import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Globe2, MonitorPlay, Star } from "lucide-react";
import { InnerNav } from "../components/InnerNav";
import { Reveal, Overline } from "../components/motion";
import { WorkCard, SectionHead } from "../components/tvui";
import { useTranslate } from "../context/TranslateContext";
import { PLATFORMS, SERIES, FILMS_ON_OTT } from "../mock/tv";

const COUNTRIES = ["India", "USA", "UK", "UAE", "Australia"];

export default function OTT() {
  const { t } = useTranslate();
  const [country, setCountry] = useState("India");
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const originals = SERIES.filter((s) => s.releaseMedium === "Streaming");
  const listenText = "OTT & Streaming. Discover original web series and films, titles arriving from cinemas, and regional content across streaming platforms — with availability by country.";

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper to-surface">
      <InnerNav />
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-12 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Overline>OTT & Streaming</Overline>
            <h1 className="mt-3 font-display text-5xl font-semibold tracking-tight text-cream md:text-7xl">{t("Stream ")}<span className="italic text-gradient-gold">{t("Indian")}</span></h1>
            <p className="mt-4 font-body text-lg text-ash">{t("Originals, web series and films arriving from cinemas — discover what's streaming and where.")}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-full border border-edge bg-surface/60 px-3 py-2">
              <Globe2 className="h-4 w-4 text-gold" />
              <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="ott-country"
                className="bg-transparent font-body text-sm text-cream focus:outline-none">
                {COUNTRIES.map((c) => <option key={c} value={c} className="bg-surface">{t(c)}</option>)}
              </select>
            </div>
            
          </div>
        </div>
        <div className="mt-3 font-body text-xs text-ash" data-testid="ott-country-note">{t("Viewing country")}: {t(country)} · {t("availability is illustrative and not verified")}</div>

        {/* Platforms */}
        <div className="mt-14">
          <SectionHead overline="Platform Directory" title={t("Platforms")} icon={MonitorPlay} accent="#3B6EA5" />
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
            {PLATFORMS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.04}>
                <Link to={`/platforms/${p.slug}`} data-testid={`platform-tile-${p.slug}`}>
                  <motion.div whileHover={{ y: -6 }} className="group flex aspect-square flex-col items-center justify-center rounded-2xl border border-edge p-4 text-center" style={{ background: p.accent + "1a" }}>
                    <div className="font-display text-2xl font-semibold" style={{ color: p.accent }}>{p.name}</div>
                    {!p.real && <div className="mt-1 rounded-full bg-black/[0.06] px-2 py-0.5 font-body text-[0.6rem] text-ash">{t("Demo")}</div>}
                    <div className="mt-1 font-body text-xs text-ash">{p.languages.length} {t("languages")}</div>
                  </motion.div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Originals */}
        <div className="mt-16">
          <SectionHead overline="Web Series & Originals" title={t("OTT Originals")} icon={Play} accent="#8E44AD" />
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {originals.map((s, i) => <WorkCard key={s.slug} s={s} i={i} />)}
          </div>
        </div>

        {/* Films now streaming — one canonical movie record across mediums */}
        <div className="mt-16">
          <SectionHead overline="From Cinemas to Streaming" title={t("Films Now Streaming")} accent="#E8873A" />
          <p className="-mt-4 mb-6 font-body text-sm text-ash">{t("These remain the same film records — one canonical entry across theatres, TV and OTT.")}</p>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {FILMS_ON_OTT.map((m, i) => (
              <Link key={m.id} to={`/movie/${m.id}`} data-testid={`ott-film-${m.id}`}>
                <motion.div whileHover={{ y: -6 }} className="group overflow-hidden rounded-2xl border border-edge bg-surface/40">
                  <div className="relative aspect-[2/3] overflow-hidden">
                    <img src={m.image} alt={m.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-paper/85 px-2 py-1 backdrop-blur"><Star className="h-3 w-3 fill-gold text-gold" /><span className="font-body text-xs text-cream">{m.rating}</span></div>
                  </div>
                  <div className="p-3"><div className="font-display text-lg text-cream">{m.title}</div><div className="font-body text-xs text-ash">{m.language} · {t("Film")}</div></div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
